import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Eye, Pencil, RotateCcw, Send, Trash2 } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../../../components/Input/Input";
import APITable from "../../../components/Tables/APITable";
import Pagination from "../../../components/Pagination/Pagination";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import ToggleSwitch from "../../../components/ToggleSwitch/ToggleSwitch";
import ViewUserModal from "../../../components/UserManagement/ViewUserModal.jsx";
import Notification from "../../../components/Notification/Notification";
import { TableSkeleton } from "../../Skeleton/StatsSkeleton";

/* ===================== SERVICES ===================== */
import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiPut,
} from "../../../apiService";
import { getErrorMessage } from "../../../utils/errorHelper";
import { can } from "../../../utils/permissions";

/* ===================== STYLES ===================== */
import styles from "./ManagementTable.module.scss";

const ManagementTable = ({ activeTab, setActiveTab, refreshKey }) => {
  const [viewUserModal, setViewUserModal] = useState({
    open: false,
    user: null,
  });
  const navigate = useNavigate();

  const currentUser = JSON.parse(localStorage.getItem("user"));
  const currentUserId = currentUser?.id;

  const [users, setUsers] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [internalLoading, setInternalLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [status, setStatus] = useState("");
  const [roleId, setRoleId] = useState("");

  const [searchInputByTab, setSearchInputByTab] = useState({
    "client-users": "",
    "internal-users": "",
  });

  const [appliedSearchByTab, setAppliedSearchByTab] = useState({
    "client-users": "",
    "internal-users": "",
  });

  const searchInput = searchInputByTab[activeTab] || "";
  const appliedSearch = appliedSearchByTab[activeTab] || "";
  const [roles, setRoles] = useState([]);

  // Confirm modal
  const [statusModal, setStatusModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [testUserModal, setTestUserModal] = useState(null);
  const [restoreModal, setRestoreModal] = useState(null);
  const [resendModal, setResendModal] = useState(null);

  // Toast
  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const notify = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3500);
  };

  // "Deleted" is a virtual status: it maps to the deleted=true query flag.
  const showingDeleted = status === "Deleted";

  // Granted actions on the "users" resource (from /modules/sidebar)
  const canDo = (action) => can("users", action);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    totalPages: 1,
  });

  // Fetch Roles
  const fetchRoles = async () => {
    try {
      const type = activeTab === "internal-users" ? "Internal" : "Client";
      const res = await apiGet("user", `/role?type=${type}`);
      if (res?.data) {
        setRoles(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch roles", err);
    }
  };

  // Fetch Users
  const fetchUsers = async (tab) => {
    if (tab === "client-users") {
      setClientLoading(true);
    } else {
      setInternalLoading(true);
    }

    setError(null);

    try {
      const query = new URLSearchParams({
        page,
        limit,
        ...(appliedSearch && { search: appliedSearch }),
        ...(status && status !== "Deleted" && { status }),
        ...(status === "Deleted" && { deleted: "true" }),
        ...(roleId && { roleId }),
      }).toString();

      const url =
        tab === "client-users"
          ? `/admin/clients?${query}`
          : `/admin/internal-users?${query}`;

      const res = await apiGet("user", url);

      if (res?.status === "success") {
        const fetchedUsers = res.data.users.map((user) => ({
          id: user.id,
          name: user.name,
          email: user.email,
          org: user.companyName,
          role: user.role?.name || "",
          industry: user.industry || "-",
          designation: user.designation,
          status: user.status,
          created: user.createdAt,
          mobile: user.mobile,
          organization: user.organization,
          active: user.status === "Active",
          isTestUser: Boolean(user.isTestUser),
        }));

        setUsers(fetchedUsers);
        setPaginationMeta({
          total: res.data.pagination.total,
          totalPages: res.data.pagination.totalPages,
        });
      }
    } catch (err) {
      if (err?.message?.includes("403")) {
        navigate("/page-not-found", { replace: true });
        return;
      }
      setError("Failed to fetch users.");
    } finally {
      if (tab === "client-users") {
        setClientLoading(false);
      } else {
        setInternalLoading(false);
      }
    }
  };

  // Refetch on changes
  useEffect(() => {
    fetchUsers(activeTab);
  }, [activeTab, refreshKey, status, roleId, page, limit, appliedSearch]);

  // Fetch roles on tab change
  useEffect(() => {
    setStatus("");
    setRoleId("");
    setPage(1);

    setSearchInputByTab((prev) => ({
      ...prev,
      [activeTab]: "",
    }));

    setAppliedSearchByTab((prev) => ({
      ...prev,
      [activeTab]: "",
    }));

    fetchRoles();
  }, [activeTab]);

  const tableData = users.map((item) => ({
    ...item,
    avatar: true,
    avatarText: item.name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2)
      .toUpperCase(),
    onToggle: item.status === "Pending" ? () => {} : () => setStatusModal(item),
    onView: () => setViewUserModal({ open: true, user: item }),
    onEdit: () => navigate(`/admin/users-management/edit/${item.id}`),
    onDelete: () => setDeleteModal(item),
    onToggleTestUser: () => setTestUserModal(item),
    onRestore: () => setRestoreModal(item),
    onResendInvite: () => setResendModal(item),
  }));

  const confirmToggle = async () => {
    if (!statusModal) return;

    const user = statusModal;
    const newStatus = user.active ? "Inactive" : "Active";

    try {
      await apiPut("user", "/users/status", {
        user_id: user.id,
        status: newStatus,
      });

      // If current logged-in user is deactivated
      if (user.id === currentUserId && newStatus === "Inactive") {
        localStorage.clear();
        navigate("/login", { replace: true });
        return;
      }

      fetchUsers(activeTab);
    } catch (error) {
      console.error("Failed to update user status", error);
      notify(getErrorMessage(error, "Failed to update user status"), "error");
    } finally {
      setStatusModal(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteModal) return;

    try {
      const res = await apiDelete("user", `/users/${deleteModal.id}`);
      if (res?.status === "success") {
        if (deleteModal.id === currentUserId) {
          localStorage.clear();
          navigate("/login", { replace: true });
          return;
        }

        fetchUsers(activeTab);
      } else {
        notify(res?.message || "Failed to delete user", "error");
      }
    } catch (error) {
      console.error("Failed to delete user", error);
      notify(getErrorMessage(error, "Failed to delete user"), "error");
    } finally {
      setDeleteModal(null);
    }
  };

  const confirmTestUserToggle = async () => {
    if (!testUserModal) return;

    const target = testUserModal;
    const nextValue = !target.isTestUser;
    const previousUsers = users;

    // Optimistic UI update
    setUsers((prev) =>
      prev.map((u) =>
        u.id === target.id ? { ...u, isTestUser: nextValue } : u,
      ),
    );

    try {
      const res = await apiPatch("user", `/users/${target.id}/test-user`, {
        isTestUser: nextValue,
      });

      if (res?.status !== "success") {
        throw new Error(res?.message || "Failed to update test user flag");
      }
    } catch (error) {
      console.error("Failed to update test user flag", error);
      notify(getErrorMessage(error, "Failed to update test user flag"), "error");
      // Roll back on failure
      setUsers(previousUsers);
    } finally {
      setTestUserModal(null);
    }
  };

  const confirmRestore = async () => {
    if (!restoreModal) return;

    try {
      const res = await apiPost("user", `/users/${restoreModal.id}/restore`);
      if (res?.status === "success") {
        notify(res?.message || "User restored successfully", "success");
        fetchUsers(activeTab);
      } else {
        notify(res?.message || "Failed to restore user", "error");
      }
    } catch (error) {
      console.error("Failed to restore user", error);
      notify(getErrorMessage(error, "Failed to restore user"), "error");
    } finally {
      setRestoreModal(null);
    }
  };

  const confirmResendInvite = async () => {
    if (!resendModal) return;

    try {
      const res = await apiPost(
        "user",
        `/users/${resendModal.id}/resend-invite`,
      );
      if (res?.status === "success") {
        notify(res?.message || "Invitation resent", "success");
      } else {
        notify(res?.message || "Failed to resend invitation", "error");
      }
    } catch (error) {
      console.error("Failed to resend invitation", error);
      notify(getErrorMessage(error, "Failed to resend invitation"), "error");
    } finally {
      setResendModal(null);
    }
  };

  const columns = [
    { label: "Full Name", key: "name" },
    { label: "Email", key: "email" },
    { label: "Organization", key: "org" },
    { label: "Role", key: "role" },
    { label: "Status", key: "status" },
    { label: "Created On", key: "created" },
    ...(activeTab === "client-users" && !showingDeleted && canDo("mark-test-user")
      ? [
          {
            label: "Test User",
            key: "testUser",
            render: (row) => (
              <div data-tooltip="Mark as Test User">
                <ToggleSwitch
                  isOn={Boolean(row.isTestUser)}
                  onToggle={row.onToggleTestUser}
                />
              </div>
            ),
          },
        ]
      : []),
    {
      label: "Actions",
      key: "action",
      render: (row) =>
        showingDeleted ? (
          <div className={styles["admin-managment-table__action-row"]}>
            {canDo("restore-user") ? (
              <span
                className={styles["admin-managment-table__action-wrap"]}
                data-tooltip="Restore User"
              >
                <RotateCcw
                  size={20}
                  className={styles["admin-managment-table__view-icon"]}
                  onClick={row.onRestore}
                  aria-label="Restore User"
                />
              </span>
            ) : (
              "—"
            )}
          </div>
        ) : (
          <div className={styles["admin-managment-table__action-row"]}>
            <span
              className={styles["admin-managment-table__action-wrap"]}
              data-tooltip="View User"
            >
              <Eye
                size={20}
                className={styles["admin-managment-table__view-icon"]}
                onClick={row.onView}
                aria-label="View User"
              />
            </span>

            {canDo("update") && (
              <span
                className={styles["admin-managment-table__action-wrap"]}
                data-tooltip="Edit User"
              >
                <Pencil
                  size={20}
                  className={styles["admin-managment-table__view-icon"]}
                  onClick={row.onEdit}
                  aria-label="Edit User"
                />
              </span>
            )}

            {canDo("delete") && (
              <span
                className={styles["admin-managment-table__action-wrap"]}
                data-tooltip="Delete User"
              >
                <Trash2
                  size={20}
                  className={styles["admin-managment-table__view-icon"]}
                  onClick={row.onDelete}
                  aria-label="Delete User"
                />
              </span>
            )}

            {row.status === "Pending" && canDo("resend-invite") && (
              <span
                className={styles["admin-managment-table__action-wrap"]}
                data-tooltip="Resend Invite"
              >
                <Send
                  size={20}
                  className={styles["admin-managment-table__view-icon"]}
                  onClick={row.onResendInvite}
                  aria-label="Resend Invite"
                />
              </span>
            )}

            {row.onToggle && canDo("active-inactive-user") && (
              <div data-tooltip={row.active ? "Deactivate User" : "Activate User"}>
                <ToggleSwitch isOn={row.active} onToggle={row.onToggle} />
              </div>
            )}
          </div>
        ),
    },
  ];

  // Download CSV
  const handleDownloadCSV = () => {
    const headers =
      columns
        .filter((col) => col.key !== "action")
        .map((col) => col.label)
        .join(",") + "\n";

    const rowsData = tableData
      .map((user) =>
        [
          user.name,
          user.email,
          user.org,
          user.role,
          user.status,
          user.created,
        ].join(","),
      )
      .join("\n");

    const blob = new Blob([headers + rowsData], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download =
      activeTab === "client-users" ? "client_users.csv" : "internal_users.csv";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isLoading =
    activeTab === "client-users" ? clientLoading : internalLoading;

  return (
    <div className={styles["admin-managment-table__card"]}>
      {notification && (
        <div className={styles["admin-managment-table__toast"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      <div className={styles["admin-managment-table__header"]}>
        {/* TABS */}
        <div className={styles["admin-managment-table__tabs"]}>
          <button
            className={`${styles["admin-managment-table__tab"]} ${
              activeTab === "client-users"
                ? styles["admin-managment-table__tab--active"]
                : ""
            }`}
            onClick={() => setActiveTab("client-users")}
          >
            Client Users
          </button>
          <button
            className={`${styles["admin-managment-table__tab"]} ${
              activeTab === "internal-users"
                ? styles["admin-managment-table__tab--active"]
                : ""
            }`}
            onClick={() => setActiveTab("internal-users")}
          >
            Internal Users
          </button>
        </div>

        {/* FILTERS */}
        <div className={styles["admin-managment-table__filters"]}>
          <div className={styles["admin-managment-table__search-group"]}>
            <Input
              type="search"
              placeholder="Search Name or Email"
              value={searchInput}
              onChange={(e) =>
                setSearchInputByTab((prev) => ({
                  ...prev,
                  [activeTab]: e.target.value,
                }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setAppliedSearchByTab((prev) => ({
                    ...prev,
                    [activeTab]: searchInput,
                  }));
                  setPage(1);
                }
              }}
            />
            <button
              className={styles["admin-managment-table__search-btn"]}
              onClick={() => {
                setAppliedSearchByTab((prev) => ({
                  ...prev,
                  [activeTab]: searchInput,
                }));
                setPage(1);
              }}
            >
              Search
            </button>
          </div>

          <Input
            type="select"
            placeholder="All Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={[
              { label: "Active", value: "Active" },
              { label: "Inactive", value: "Inactive" },
              { label: "Pending", value: "Pending" },
              { label: "Deleted", value: "Deleted" },
            ]}
          />

          <Input
            type="select"
            placeholder="All Roles"
            value={roleId}
            onChange={(e) =>
              setRoleId(e.target.value ? Number(e.target.value) : "")
            }
            options={roles.map((role) => ({
              label: role.name,
              value: role.id,
            }))}
          />
        </div>
      </div>

      <div className={styles["admin-managment-table__wrapper"]}>
        <div className={styles["admin-managment-table__table-controls"]}>
          <div className={styles["admin-managment-table__showing-text"]}>
            Showing {users.length} of {paginationMeta.total} users
          </div>

          {canDo("export") && (
            <button
              className={styles["admin-managment-table__download-btn"]}
              onClick={handleDownloadCSV}
            >
              <Download size={18} />
              Download
            </button>
          )}
        </div>

        {isLoading ? (
          <TableSkeleton columns={5} rows={4} />
        ) : error ? (
          <p className={styles["admin-managment-table__error"]}>{error}</p>
        ) : (
          <APITable columns={columns} data={tableData} />
        )}
      </div>

      <Pagination
        currentPage={page}
        totalPages={paginationMeta.totalPages}
        rowsPerPage={limit}
        onPageChange={(p) => setPage(p)}
        onRowsPerPageChange={(l) => {
          setLimit(l);
          setPage(1);
        }}
      />

      <ConfirmModal
        open={!!statusModal}
        title="Are you sure you want to change the user status?"
        onClose={() => setStatusModal(null)}
        onConfirm={confirmToggle}
      />

      <ConfirmModal
        open={!!deleteModal}
        title="Are you sure you want to delete this user?"
        onClose={() => setDeleteModal(null)}
        onConfirm={confirmDelete}
      />

      <ConfirmModal
        open={!!restoreModal}
        title="Are you sure you want to restore this user?"
        onClose={() => setRestoreModal(null)}
        onConfirm={confirmRestore}
      />

      <ConfirmModal
        open={!!resendModal}
        title="Resend the invitation email to this user?"
        onClose={() => setResendModal(null)}
        onConfirm={confirmResendInvite}
      />

      <ConfirmModal
        open={!!testUserModal}
        title={
          testUserModal?.isTestUser
            ? "Remove test user flag from this user?"
            : "Mark this user as a test user?"
        }
        onClose={() => setTestUserModal(null)}
        onConfirm={confirmTestUserToggle}
      />

      <ViewUserModal
        open={viewUserModal.open}
        user={viewUserModal.user}
        onClose={() => setViewUserModal({ open: false, user: null })}
        fields={[
          { label: "Name", key: "name", value: (user) => user.name },
          {
            label: "Role",
            key: "role",
            value: (user) => user.role?.name || user.role,
          },
          { label: "Email", key: "email", value: (user) => user.email },
          { label: "Mobile", key: "mobile", value: (user) => user.mobile },
          {
            label: "Industry",
            key: "industry",
            value: (user) => user.industry,
          },
          {
            label: "Designation",
            key: "designation",
            group: "org",
            value: (user) => user.designation,
          },
          { label: "Status", key: "status", value: (user) => user.status },
          {
            label: "Created On",
            key: "created",
            value: (user) => user.created || "-",
          },
          {
            label: "Organization",
            key: "companyName",
            value: (user) => user.organization?.companyName,
          },
          {
            label: "State",
            key: "State",
            value: (user) => user.organization?.state,
          },
          {
            label: "GstIn",
            key: "gst",
            value: (user) => user.organization?.gstNo,
          },
          {
            label: "Created On",
            key: "createdAt",
            value: (user) => user.organization?.createdAt || "-",
          },
          {
            label: "Address",
            key: "Address",
            value: (user) => user.organization?.address,
          },
        ]}
      />
    </div>
  );
};

export default ManagementTable;
